import './styles.scss'
import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import { Field, Form } from 'react-final-form'
import { t } from 'i18next'
import { classNames } from 'primereact/utils'
import { InputText } from 'primereact/inputtext'
import { InputTextarea } from 'primereact/inputtextarea'
import { InputSwitch } from 'primereact/inputswitch'
import { MultiSelect } from 'primereact/multiselect'
import { Checkbox } from 'primereact/checkbox'
import { supabase } from 'Services/supabaseClient'
import apiAdapterCoffeeWeb from 'Services/apiAdapterCoffeeWeb'
import { ButtonComponent, DialogModal } from 'Components/UIComponents'
import { ROUTE_STRINGS } from 'Utils/Constants'
import LayoutContainer from 'Components/LayoutComponent'
import Loader from 'Components/Loader'

const EditCommunity = ({ setLoading }) => {
  const history = useHistory()
  const data = history?.location?.state

  const categories = [
    { name: 'Regular', key: 'R' },
    { name: 'Gold', key: 'G' },
    { name: 'Platinum', key: 'P' }
  ]

  const [selectedCategories, setSelectedCategories] = useState([])
  const [subscriptionTouched, setSubscriptionTouched] = useState(false)
  const [showDialog, setShowDialog] = useState({ showModal: false, modalCancel: false, modalType: '', message: '', onDismiss: () => {}, onRetry: () => {}, onHide: () => {}, onSuccess: () => {} })
  const [countriesList, setCountriesList] = useState([])
  const [adminOnly, setAdminOnly] = useState(data?.admin_only_message)
  const [countrySpecific, setCountrySpecific] = useState(data?.country_specific)
  const [selectedCountryIds, setSelectedCountryIds] = useState(data?.country_id || [])

  useEffect(() => {
    if (data?.required_subscription) {
      const preSelected = categories.filter((cat) => data.required_subscription.includes(cat.key))

      setSelectedCategories(preSelected)
    }
  }, [data])

  useEffect(() => {
    getAllCountriesList()
  }, [])

  const getAllCountriesList = async () => {
    setLoading(true)
    setShowDialog({ ...showDialog, showModal: false })

    try {
      const countryData = await apiAdapterCoffeeWeb.getAllCountries()

      setCountriesList(countryData.data || [])
    } catch (error) {
      setShowDialog({
        ...showDialog,
        showModal: true,
        modalType: 'error',
        message: t('SORRY_COULD_NOT_FETCH_THE_DATA_PLEASE_TRY_AGAIN_LATER'),
        onHide: handleDismiss,
        onRetry: () => getAllCountriesList()
      })
    } finally {
      setLoading(false)
    }
  }

  const onCategoryChange = (e) => {
    setSubscriptionTouched(true)

    const clickedCategory = e.value
    const isSelected = selectedCategories.some((cat) => cat.key === clickedCategory.key)

    if (isSelected) {
      setSelectedCategories([])
    } else {
      // If checked, apply your category logic
      // eslint-disable-next-line
      if (clickedCategory.name === 'Regular') {
        setSelectedCategories(categories)
      } else if (clickedCategory.name === 'Gold') {
        setSelectedCategories(categories.filter((cat) => cat.name !== 'Regular'))
      } else if (clickedCategory.name === 'Platinum') {
        setSelectedCategories(categories.filter((cat) => cat.name === 'Platinum'))
      }
    }
  }

  const handleDismiss = () => {
    setShowDialog({ ...showDialog, showModal: false })
  }

  const onSubmit = async (values) => {
    setShowDialog({ ...showDialog, showModal: false })
    setLoading(true)

    try {
      const subscriptions = selectedCategories.map((category) => category.key)

      const payload = {
        id: data?.id,
        name: values.communityName,
        community_url: values.communityLogoLink,
        description: values.description,
        required_subscription: subscriptions,
        default_sort_order: Number(values.default_sort_order),
        admin_only_message: adminOnly,
        country_specific: countrySpecific,
        country_id: countrySpecific ? selectedCountryIds : []
      }

      const { data: updatedCommunity, error, status } = await supabase.from('communities').update(payload).eq('id', data?.id).select('*').single()

      if (status === 200 && updatedCommunity.length !== 0) {
        setShowDialog({
          ...showDialog,
          showModal: true,
          modalType: 'success',
          message: 'COMMUNITY_UPDATED_SUCCESSFUL',
          modalCancel: false,
          onSuccess: () => history.push(ROUTE_STRINGS.communityManagement)
        })
      }

      if (error) {
        setShowDialog({
          ...showDialog,
          showModal: true,
          modalType: 'error',
          message: t('FAILED_TO_UPDATE_COMMUNITY_LIST'),
          modalCancel: false,
          onDismiss: handleDismiss,
          onRetry: () => onSubmit(values)
        })
      }
    } catch (err) {
      setShowDialog({
        ...showDialog,
        showModal: true,
        modalType: 'error',
        message: t('FAILED_TO_UPDATE_COMMUNITY_LIST'),
        modalCancel: false,
        onDismiss: handleDismiss,
        onRetry: () => onSubmit(values)
      })
    } finally {
      setLoading(false)
    }
  }

  const validate = (values) => {
    const errors = {}

    if (!values.communityName) {
      errors.communityName = t('COMMUNITY_NAME_REQUIRED')
    }

    return errors
  }

  const handleCancel = () => {
    history.push({
      pathname: ROUTE_STRINGS.communityManagement
    })
  }

  const isFormFieldValid = (meta) => !!(meta.touched && meta.error)
  const getFormErrorMessage = (meta) => isFormFieldValid(meta) && <small className="p-error">{meta.error}</small>

  const initialValues = {
    communityName: data?.name || '',
    communityLogoLink: data?.community_url || '',
    description: data?.description || '',
    default_sort_order: data?.default_sort_order || ''
  }

  const isFormChanged = (values) => {
    const normalize = (val) => (val ?? '').trim()

    return normalize(values.communityName) !== normalize(initialValues.communityName) || normalize(values.communityLogoLink) !== normalize(initialValues.communityLogoLink) || normalize(values.description) !== normalize(initialValues.description) || String(values.default_sort_order) !== String(initialValues.default_sort_order)
  }

  const hasSubscriptionChanged = () => {
    const original = (data?.required_subscription || []).slice().sort().join(',')
    const current = selectedCategories
      .map((cat) => cat.key)
      .slice()
      .sort()
      .join(',')

    return original !== current
  }

  const handleAdminOnlyChange = (e) => {
    setAdminOnly(e.value)
  }

  const handleCountrySpecificChange = (e) => {
    const newVal = e.value

    setCountrySpecific(newVal)
  }

  const handleSelectedCountriesChange = (e) => {
    console.log(e.value)

    setSelectedCountryIds(e.value)
  }

  const countryOptionTemplate = (country) => {
    if (!country) return null

    return (
      <div className="country-chip-option-template">
        <img alt={country?.nicename} src={country?.flagsUrl} className="flag-img" />
        <span className="country-name">{country?.nicename}</span>
      </div>
    )
  }

  const selectedCountryTemplate = (country) => {
    if (!country) return 'Select Country'

    return (
      <div className="country-chip-value-template">
        <img alt={country?.nicename} src={country?.flagsUrl} className="flag-img" />
        <span className="country-name">{country?.nicename}</span>
      </div>
    )
  }

  return (
    <>
      <LayoutContainer title={t('EDIT_COMMUNITY')} breadCrumbsTitle={'editCommunity'}>
        <div className="edit-community-form">
          <Form
            onSubmit={onSubmit}
            validate={validate}
            initialValues={initialValues}
            render={({ handleSubmit, values, ...rest }) => {
              const { dirty, valid } = rest

              const submitForm = (e) => {
                e.preventDefault()
                handleSubmit()
              }

              const isSubmitEnabled = (isFormChanged(values) || hasSubscriptionChanged() || adminOnly !== data?.admin_only_message || countrySpecific !== data?.country_specific || (countrySpecific && JSON.stringify(selectedCountryIds.sort()) !== JSON.stringify((data?.country_id || []).slice().sort()))) && valid && selectedCategories.length > 0

              return (
                <>
                  <form onSubmit={handleSubmit} className="form-container">
                    {/* Community Name */}
                    <Field
                      name="communityName"
                      render={({ input, meta }) => (
                        <div className="field">
                          <span className="p-float-label">
                            <InputText id="communityName" {...input} className={classNames(isFormFieldValid(meta) ? 'error' : 'add-menu-field')} />
                            <label htmlFor="communityName" className={classNames(isFormFieldValid(meta) ? 'p-error' : 'mapping-label')}>
                              {t('COMMUNITY_NAME')} <span className="star-required">*</span>
                            </label>
                          </span>
                          {getFormErrorMessage(meta) && <div className="error-message">{getFormErrorMessage(meta)}</div>}
                        </div>
                      )}
                    />

                    {/* Community Logo Link */}
                    <Field
                      name="communityLogoLink"
                      render={({ input, meta }) => (
                        <div className="field">
                          <span className="p-float-label">
                            <InputText id="communityLogoLink" {...input} className={classNames(isFormFieldValid(meta) ? 'error' : 'add-menu-field')} />
                            <label htmlFor="communityLogoLink" className={classNames(isFormFieldValid(meta) ? 'p-error' : 'mapping-label')}>
                              {t('COMMUNITY_LOGO_LINK')}
                            </label>
                          </span>
                          {getFormErrorMessage(meta) && <div className="error-message">{getFormErrorMessage(meta)}</div>}
                        </div>
                      )}
                    />

                    {/* Description */}
                    <Field
                      name="description"
                      render={({ input, meta }) => (
                        <div className="field">
                          <span className="p-float-label">
                            <InputTextarea id="description" {...input} rows={5} cols={30} className={classNames(isFormFieldValid(meta) ? 'error' : 'add-menu-field')} placeholder={t('DESCRIPTION')} />
                            <label htmlFor="description" className={classNames(isFormFieldValid(meta) ? 'p-error' : 'mapping-label')}>
                              {t('DESCRIPTION')}
                            </label>
                          </span>
                          {getFormErrorMessage(meta) && <div className="error-message">{getFormErrorMessage(meta)}</div>}
                        </div>
                      )}
                    />

                    {/* Default Sort Order */}
                    <Field
                      name="default_sort_order"
                      render={({ input, meta }) => (
                        <div className="field">
                          <span className="p-float-label">
                            <InputText id="default_sort_order" {...input} className={classNames(isFormFieldValid(meta) ? 'error' : 'add-menu-field')} keyfilter="int" />
                            <label htmlFor="default_sort_order" className={classNames(isFormFieldValid(meta) ? 'p-error' : 'mapping-label')}>
                              {t('DEFAULT_SORT_ORDER')}
                            </label>
                          </span>
                          {getFormErrorMessage(meta) && <div className="error-message">{getFormErrorMessage(meta)}</div>}
                        </div>
                      )}
                    />

                    {/* Subscription Category Checkboxes */}
                    <div className="subscription-div">
                      <div className="subscription-checkbox-div">
                        <span>{t('SUBSCRIPTIONS')}</span> <span className="mandate">*</span>
                        {categories.map((category) => (
                          <div key={category.key} className="subscription-checkbox">
                            <Checkbox inputId={category.key} name="category" value={category} onChange={onCategoryChange} checked={selectedCategories.some((item) => item.key === category.key)} className="checkbox-field" />
                            <label htmlFor={category.key} className="checkbox-label">
                              {category.name}
                            </label>
                          </div>
                        ))}
                        {subscriptionTouched && selectedCategories.length === 0 && <div className="checkbox-error-message">{t('SUBSCRIPTION_REQUIRED')}</div>}
                      </div>
                    </div>

                    {/* Admin only message toggle */}
                    <div className="field toggle-field">
                      <label className="mapping-label">{t('ADMIN_ONLY_MESSAGES')}</label>
                      <InputSwitch checked={adminOnly} onChange={handleAdminOnlyChange} aria-label="admin-only-switch" />
                    </div>

                    {/* Country specific toggle */}
                    <div className="field toggle-field">
                      <label className="mapping-label">{t('COUNTRY_SPECIFIC')}</label>
                      <InputSwitch checked={countrySpecific} onChange={handleCountrySpecificChange} aria-label="country-specific-switch" />
                    </div>

                    {/* Multiselect visible only when countrySpecific is true */}
                    {countrySpecific && (
                      <div className="field">
                        <label className="mapping-label" style={{ display: 'block', marginBottom: 6 }}>
                          {t('SELECT_COUNTRIES')}
                        </label>

                        <MultiSelect value={selectedCountryIds} options={countriesList} onChange={handleSelectedCountriesChange} placeholder={t('SELECT_COUNTRY')} className="country-multiselect" maxSelectedLabels={3} optionLabel="nicename" optionValue="id" filter itemTemplate={(country) => countryOptionTemplate(country)} valueTemplate={(country) => selectedCountryTemplate(country)} />

                        {countrySpecific && selectedCountryIds.length === 0 && <div className="error-message">{t('PLEASE_SELECT_AT_LEAST_ONE_COUNTRY')}</div>}
                      </div>
                    )}
                  </form>
                  <div className="edit-community-footer-container">
                    <ButtonComponent type="cancel" onClick={handleCancel} />
                    <ButtonComponent type="submit" onClick={submitForm} disabled={!isSubmitEnabled} />
                  </div>
                </>
              )
            }}
          />
        </div>
      </LayoutContainer>
      <DialogModal {...showDialog} onDismiss={handleDismiss} />
    </>
  )
}

export default Loader(EditCommunity)
